/* Churches — the console's home.
 *
 * The client's specification calls this screen "Groups" and its rows Groups.
 * We say Church, consistently, everywhere a person can read it; the API path
 * stays /groups. docs/CLIENT_TERMINOLOGY.md maps every screen back to their
 * wording.
 *
 * STATUS IS A TAB, NOT A FILTER DROPDOWN. The four states are the working
 * shape of the job — active churches, ones on hold, ones deleted, ones still
 * waiting for their admin to register — and a tab makes the counts visible
 * without a click.
 *
 * Paging is server-side: a platform with three hundred churches should not
 * send all of them to render twenty.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Plus, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useChurches } from '@/api/console'
import { useTabParam } from '@/lib/useTabParam'
import type { ChurchStatus, ChurchSummary } from '@/types/api'

import { StatusPill } from './StatusPill'

const TABS = ['all', 'active', 'on_hold', 'awaiting_admin', 'deleted'] as const
type Tab = (typeof TABS)[number]

/* The client's tabs read "All groups | Groups on Hold | Deleted Groups"; the
   same shape, with church for group. */
const TAB_LABEL: Record<Tab, string> = {
  all: 'All churches',
  active: 'Active churches',
  on_hold: 'Churches on hold',
  awaiting_admin: 'Awaiting admin',
  deleted: 'Deleted churches',
}

const EMPTY: Record<Tab, string> = {
  all: 'No churches yet.',
  active: 'No active churches.',
  on_hold: 'No churches on hold.',
  awaiting_admin: 'No church is waiting for its admin.',
  deleted: 'No deleted churches.',
}

const PAGE_SIZE = 20

export function ChurchesPage() {
  const [tab, setTab] = useTabParam<Tab>(TABS, 'all', 'status')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const churches = useChurches({
    status: tab as ChurchStatus | 'all',
    search,
    page,
    pageSize: PAGE_SIZE,
  })

  const items = churches.data?.items ?? []
  const total = churches.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  /* Changing the tab or the search resets to page one. Without this, filtering
     from page 4 of "All" down to two on-hold churches shows an empty page 4
     and looks like there are none. */
  function changeTab(next: Tab) {
    setTab(next)
    setPage(1)
  }
  function changeSearch(next: string) {
    setSearch(next)
    setPage(1)
  }

  return (
    <div className="space-y-s4">
      <header className="flex flex-wrap items-end justify-between gap-s3">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Churches</h1>
          <p className="mt-1 text-base text-ink-2">
            {churches.isLoading ? '…' : `${total} ${total === 1 ? 'church' : 'churches'}`}
          </p>
        </div>
        {/* The icon and label are wrapped in one span: with asChild, Button
            renders through Radix's Slot, which takes exactly one child — an
            icon and text side by side make two, and it throws. */}
        <Button variant="primary" asChild>
          <Link to="/churches/new">
            <span className="flex items-center gap-s2">
              <Plus className="h-4 w-4" aria-hidden />
              Add church
            </span>
          </Link>
        </Button>
      </header>

      <Card className="flex flex-wrap items-center gap-s3 p-s3">
        <div className="flex flex-wrap gap-s1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => changeTab(t)}
              aria-current={tab === t ? 'page' : undefined}
              className={
                'rounded-pill px-s3 py-s2 text-base font-semibold transition-colors ' +
                (tab === t
                  ? 'bg-brand text-white'
                  : 'text-ink-2 hover:bg-hover')
              }
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>

        <div className="relative ml-auto min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => changeSearch(e.target.value)}
            placeholder="Search by name or city"
            aria-label="Search churches"
            className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
          />
        </div>
      </Card>

      {churches.isError ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          Could not load churches. Check your connection and try again.
        </Card>
      ) : null}

      {!churches.isError && !churches.isLoading && !items.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {search ? `No church matches “${search}”.` : EMPTY[tab]}
        </Card>
      ) : null}

      {/* PHONE: cards, not a table.
          A five-column table at 375px either scrolls sideways or wraps every
          church name onto three lines — both were true before this. Below md
          each church is a card that says the same things in reading order. */}
      {items.length ? (
        <div className="space-y-s2 md:hidden">
          {items.map((c) => (
            <Card key={c.id} className="p-s3">
              <div className="flex items-start justify-between gap-s3">
                <div className="min-w-0">
                  <Link
                    to={`/churches/${c.id}`}
                    className="block font-semibold text-brand hover:underline"
                  >
                    {c.name}
                  </Link>
                  <p className="mt-0.5 text-sm text-ink-2">
                    {[c.city, `${c.member_count} ${c.member_count === 1 ? 'member' : 'members'}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <StatusPill status={c.status} />
              </div>
              <Button size="sm" full className="mt-s3" asChild>
                <Link to={`/churches/${c.id}`}>
                  <span className="flex items-center justify-center gap-s2">
                    Manage
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              </Button>
            </Card>
          ))}
        </div>
      ) : null}

      {items.length ? (
        <Card className="hidden overflow-hidden p-0 md:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line-2 text-left text-sm text-ink-2">
                  <th className="px-s4 py-s3 font-semibold">CHURCH</th>
                  {/* Admin and Plan drop out first on a narrow screen: the
                      contact is on the church's own page, and the plan is the
                      least urgent thing in the row. */}
                  <th className="px-s4 py-s3 font-semibold">ADMIN</th>
                  <th className="px-s4 py-s3 font-semibold">MEMBERS</th>
                  <th className="hidden px-s4 py-s3 font-semibold lg:table-cell">PLAN</th>
                  <th className="px-s4 py-s3 font-semibold">STATUS</th>
                  <th className="px-s4 py-s3 text-right font-semibold">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <ChurchRow key={c.id} church={c} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between gap-s3">
          <span className="text-base text-ink-2">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-s2">
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ChurchRow({ church }: { church: ChurchSummary }) {
  return (
    <tr className="border-b border-line-2 last:border-0 hover:bg-hover">
      <td className="px-s4 py-s3">
        <Link
          to={`/churches/${church.id}`}
          className="font-semibold text-brand hover:underline"
        >
          {church.name}
        </Link>
        {church.city ? (
          <span className="block text-sm text-ink-2">{church.city}</span>
        ) : null}
      </td>

      <td className="px-s4 py-s3 text-base text-ink-2">
        {church.admin_mobile ?? church.admin_email ?? (
          /* Not "—". A church with no admin is the thing this console exists
             to notice, so it says so. */
          <span className="text-warning-ink">Not registered</span>
        )}
      </td>

      <td className="px-s4 py-s3 text-base">{church.member_count}</td>
      <td className="hidden px-s4 py-s3 text-base capitalize text-ink-2 lg:table-cell">
        {church.plan}
      </td>
      <td className="px-s4 py-s3">
        <StatusPill status={church.status} />
      </td>

      {/* The row's action. The church name is a link too, but a name that
          happens to be clickable is a convention people have to learn — an
          explicit control at the end of the row is one they already know. */}
      <td className="px-s4 py-s3 text-right">
        <Button size="sm" asChild>
          <Link to={`/churches/${church.id}`}>
            <span className="flex items-center gap-s2">
              Manage
              <ChevronRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        </Button>
      </td>
    </tr>
  )
}
